import { List, Map } from 'immutable'
import { AsyncState, HttpResult, loadingAsyncState } from 'widgets-for-react'
import { ImageSize } from '../cms/blocks/types.gen'
import { Tags } from '../publicSite/overview/state'
import { tagToTaxonomy } from '../publicSite/overview/utils'
import { Breadcrumb } from '../publicSite/templates/breadcrumbs.mod'
import { defaultLanguage, removeLangFromUrl, updateUrlLang } from '../publicSite/utils/history.mod'
import { Locale, Pager, PaginatedItem, ParsedMenuItem } from './types_and_parser/extra_types.mod'

export async function getPressReleases<PressReleaseItem>(): Promise<HttpResult<PaginatedItem<PressReleaseItem>>> {
  const res = await fetch('/api/v1/globenewswire')

  if (!res.ok) return { kind: 'failed', status: 400 }

  const json = await res.json()
  const pager = json['pager'] as Pager
  const items = json['items']

  let parsed: PaginatedItem<PressReleaseItem>

  parsed = {
    pager: pager,
    items: List(json)
  }
  return { kind: 'result', status: 200, value: parsed }
}

type MenuItem = {
  key: string
  title: string
  description: string
  uri: string
  alias: string
  external: boolean
  absolute: string
  relative: string | null
  weight: string
  expanded: boolean
  enabled: boolean
  uuid: string | null
  options: any
  below?: MenuItem[]
}

export const parse_menu = (t: MenuItem[]): List<ParsedMenuItem> => List(t.map(menu_item_parser))

const menu_item_parser = (t: MenuItem): ParsedMenuItem => ({
  key: t.key,
  label: t.title,
  relative: !!t.relative,
  url: t.relative ? t.relative : t.absolute,
  children: t.below ? List(t.below.map(menu_item_parser)) : null
})

export const getTopMenu = async (lang: Locale): Promise<HttpResult<List<ParsedMenuItem>>> => {
  const menuList =
    window.__PRELOADED_DATA__ && window.__PRELOADED_DATA__.menus && window.__PRELOADED_DATA__.menus.top
      ? window.__PRELOADED_DATA__.menus.top
      : await (
          await fetch(`/api/drupal${lang == 'global' ? '' : `/${lang}`}/api/menu_items/top`, { credentials: 'include' })
        ).json()

  return {
    kind: 'result',
    status: 200,
    value: parse_menu(menuList)
  }
}

export const getCountryMenu = async (lang: Locale): Promise<HttpResult<List<ParsedMenuItem>>> => {
  const menuList =
    window.__PRELOADED_DATA__ && window.__PRELOADED_DATA__.menus && window.__PRELOADED_DATA__.menus.top
      ? window.__PRELOADED_DATA__.menus.top
      : await (
          await fetch(`/api/drupal${lang == 'global' ? '' : `/${lang}`}/api/menu_items/country`, {
            credentials: 'include'
          })
        ).json()

  return {
    kind: 'result',
    status: 200,
    value: parse_menu(menuList)
  }
}

type PreloadedData = {
  menus?: { top: any; main: any; footer: any; privacy: any }
}

declare global {
  interface Window {
    __PRELOADED_DATA__: PreloadedData
  }
}

window.__PRELOADED_DATA__ = window.__PRELOADED_DATA__ || {}

export const getHeader = async (lang: Locale): Promise<HttpResult<List<ParsedMenuItem>>> => {
  const menuList =
    window.__PRELOADED_DATA__ && window.__PRELOADED_DATA__.menus && window.__PRELOADED_DATA__.menus.main
      ? window.__PRELOADED_DATA__.menus.main
      : await (
          await fetch(`/api/drupal${lang == 'global' ? '' : `/${lang}`}/api/menu_items/main`, {
            credentials: 'include'
          })
        ).json()

  return {
    kind: 'result',
    status: 200,
    value: parse_menu(menuList)
  }
}

const getBreadcrumbs = async (alias: string, lang: Locale): Promise<HttpResult<Array<Breadcrumb>>> => {
  const breadcrumbCall = await fetch(
    alias != undefined
      ? `/api/drupal${lang == 'global' ? '' : `/${lang}`}/api/breadcrumb?path=${removeLangFromUrl(alias)}`
      : '/s',
    {
      method: 'GET',
      credentials: 'include'
    }
  )
  let breadcrumbs = (await (await breadcrumbCall).json()) as Breadcrumb[]
  breadcrumbs = breadcrumbs.map((b) => ({ ...b, url: updateUrlLang(b.url, lang) }))

  return {
    kind: 'result',
    status: 200,
    value: breadcrumbs
  }
}

export const loadBreadcrumbs = (alias: string, lang: Locale): AsyncState<Array<Breadcrumb>> => {
  return loadingAsyncState((_) =>
    getBreadcrumbs(alias, lang).then<HttpResult<Array<Breadcrumb>>>((bread) => {
      return bread.kind == 'result'
        ? { kind: 'result', value: bread.value.length === 1 ? bread.value : bread.value, status: 200 }
        : { kind: 'failed', status: 404 }
    })
  )
}

export const mockedImageStyleMap = (url: string) =>
  Map<ImageSize, string>(Array<ImageSize>('thumbnail', 'medium', 'large').map((i) => [i, url]))

export type PaginatedItemStone<T> = {
  items: List<T>
  pager: PagerStone
}
export type PagerStone = {
  currentPage: number
  totalItems: number
  totalPages: number
  itemsPerPage: number
}

export const getTags = async (lang: string, taxonomy: string, selectedTags: Tags): Promise<HttpResult<string[]>> => {
  const url =
    lang && lang.length > 0 && lang != defaultLanguage
      ? `/api/v1/${lang}/GetTags/${taxonomy}`
      : `/api/v1/GetTags/${taxonomy}`
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    method: 'post',
    credentials: 'include',
    body: JSON.stringify({
      taxonomies_to_filter: selectedTags
        .map((v, k) => ({
          taxonomy_title: tagToTaxonomy(k),
          taxonomy_items_to_select: v ? v.filter((t) => t != '').map((t) => htmlDecode(t)) : []
        }))
        .valueSeq()
        .toArray()
    })
  })

  if (!response.ok) return { kind: 'result', status: 200, value: [] }

  try {
    const json = await response.json()
    return { kind: 'result', status: 200, value: json.map((v) => htmlDecode(v)) }
  } catch (e) {
    console.error(e)
    return { kind: 'failed', status: 500 }
  }
}

export function htmlDecode(input) {
  try {
    var doc = new DOMParser().parseFromString(input, 'text/html')
    return doc.documentElement.textContent
  } catch (e) {
    console.error(e)
    return input
  }
}
